import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LocalizedLink as Link, stripLangPrefix, localizePath } from "@/i18n/routing";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { trackConversion } from "@/lib/analytics";
import { Menu, X, ChevronDown, Mail, Instagram, Facebook, ArrowRight, Download } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/2v7vadbi_e-mailhantekening_64b76e53-7295-48c0-a264-c3f8aef367e6.jpg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_ITEMS = [
    { key: "craftDreamVilla", href: "/villas-for-sale" },
    { key: "turnkeyForSale", href: "/turnkey-villa" },
    { key: "hotelRentalProgram", href: "/hotel-rental-program" },
    { key: "destination", href: "/destination" },
    { key: "aboutUs", href: "/about" },
    { key: "contact", href: "/contact" },
    { key: "gallery", href: "/gallery" },
    { key: "press", href: "/press" },
];

const LANG_LABEL = { en: "ENG", fr: "FR", nl: "NL", pt: "PT" };

export function SiteHeader() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const lang = i18n.language?.slice(0, 2) || "en";

    return (
        <>
            <header
                data-testid="top-banner"
                className="fixed top-0 left-0 right-0 z-20 bg-[#f3ead9] grid grid-cols-[1fr_auto_1fr] items-center px-4 md:px-10 lg:px-14 border-b border-[#e5d9bf] h-[68px] md:h-[84px]"
            >
                <div className="flex items-center gap-3 md:gap-6 min-w-0 justify-self-start">
                    <button
                        type="button"
                        data-testid="menu-button"
                        aria-label="Ouvrir le menu"
                        aria-expanded={menuOpen}
                        onClick={() => {
                            setMenuOpen((v) => !v);
                            setLangOpen(false);
                        }}
                        className="group flex items-center gap-2 text-[#1a1a18] hover:text-[#7a4a3a] transition-colors py-2"
                    >
                        {menuOpen ? <X size={18} strokeWidth={1.4} /> : <Menu size={18} strokeWidth={1.4} />}
                        <span className="hidden sm:inline text-[11px] md:text-[12px] uppercase tracking-[0.22em] md:tracking-[0.28em] font-light">
                            {t("common.menu")}
                        </span>
                    </button>

                    <div className="relative">
                        <button
                            type="button"
                            data-testid="lang-toggle"
                            aria-label="Choisir la langue"
                            aria-expanded={langOpen}
                            onClick={() => {
                                setLangOpen((v) => !v);
                                setMenuOpen(false);
                            }}
                            className="group flex items-center gap-2 text-[#1a1a18] hover:text-[#7a4a3a] transition-colors py-2"
                        >
                            <span className="text-[11px] md:text-[12px] uppercase tracking-[0.28em] font-light">
                                {LANG_LABEL[lang] || "ENG"}
                            </span>
                            <ChevronDown
                                size={14}
                                strokeWidth={1.4}
                                className={`transition-transform duration-300 ${langOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {langOpen && (
                            <ul className="absolute top-full left-0 mt-3 min-w-[110px] bg-[#f3ead9] border border-[#e5d9bf] shadow-md py-1 z-30">
                                {["en", "nl", "fr", "pt"].map((l) => (
                                    <li key={l}>
                                        <button
                                            type="button"
                                            data-testid={`lang-option-${l}`}
                                            onClick={() => {
                                                i18n.changeLanguage(l);
                                                setLangOpen(false);
                                                navigate(localizePath(stripLangPrefix(location.pathname), l), { replace: true });
                                            }}
                                            className={`w-full text-left px-4 py-2 text-[11px] uppercase tracking-[0.28em] font-light transition-colors ${
                                                lang === l
                                                    ? "text-[#7a4a3a] bg-[#e5d9bf]/40"
                                                    : "text-[#1a1a18] hover:bg-[#e5d9bf]/40"
                                            }`}
                                        >
                                            {LANG_LABEL[l]}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <Link
                    to="/"
                    data-testid="banner-logo"
                    aria-label="Barefoot Luxury"
                    className="justify-self-center w-28 sm:w-40 md:w-64 lg:w-72 h-[68px] md:h-[84px] mix-blend-multiply overflow-hidden"
                    style={{
                        backgroundImage: `url(${LOGO_URL})`,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "auto 128%",
                        backgroundPosition: "center -12px",
                    }}
                />

                <nav
                    aria-label="Réseaux sociaux & contact"
                    className="flex items-center justify-self-end gap-3 md:gap-5 text-[#1a1a18] min-w-0"
                >
                    <a href="mailto:stef@barefootluxury.world" aria-label="Email" className="hover:text-[#7a4a3a] transition-colors">
                        <Mail size={18} strokeWidth={1.4} />
                    </a>
                    <a
                        href="https://wa.me/32475838300"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="WhatsApp"
                        className="hover:text-[#7a4a3a] transition-colors"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                            width="18"
                            height="18"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M27 15.5c0 6.075-4.925 11-11 11a10.96 10.96 0 0 1-5.252-1.341L5 27l1.86-5.65A10.96 10.96 0 0 1 5 15.5C5 9.425 9.925 4.5 16 4.5s11 4.925 11 11Z" />
                            <path d="M12 11.5c0 3.6 4.4 8 8 8 .8 0 1.5-.7 1.5-1.5 0-.4-.2-.7-.5-.9l-1.8-.9c-.3-.2-.7-.1-.9.2l-.5.6c-1.4-.4-2.5-1.5-2.9-2.9l.6-.5c.3-.2.4-.6.2-.9l-.9-1.8c-.2-.3-.5-.5-.9-.5-.8 0-1.5.7-1.5 1.5Z" />
                        </svg>
                    </a>
                    <a href="https://www.instagram.com/barefootluxury_saovicente/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#7a4a3a] transition-colors">
                        <Instagram size={18} strokeWidth={1.4} />
                    </a>
                    <a href="https://www.facebook.com/p/Barefoot-Luxury-S%C3%A3o-Vicente-61564999997147/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[#7a4a3a] transition-colors">
                        <Facebook size={18} strokeWidth={1.4} />
                    </a>
                </nav>
            </header>

            {/* Drawer */}
            <div
                data-testid="menu-drawer"
                className={`fixed top-[68px] md:top-[84px] left-0 right-0 z-10 bg-[#f3ead9]/95 backdrop-blur-md border-b border-[#e5d9bf] transition-all duration-500 ${
                    menuOpen
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-4 pointer-events-none"
                }`}
            >
                <ul className="max-w-4xl mx-auto px-6 md:px-10 py-8 flex flex-col gap-5">
                    {MENU_ITEMS.map((item) => {
                        const isRoute = item.href.startsWith("/") && !item.href.includes("#");
                        const label = t(`nav.${item.key}`);
                        const cls = "text-[#1a1a18] text-2xl md:text-3xl font-light tracking-wide hover:text-[#7a4a3a] transition-colors";
                        const style = { fontFamily: '"Cormorant Garamond", "Times New Roman", serif' };
                        return (
                            <li key={item.key}>
                                {isRoute ? (
                                    <Link to={item.href} data-testid={`menu-link-${item.key}`} onClick={() => setMenuOpen(false)} className={cls} style={style}>{label}</Link>
                                ) : (
                                    <a href={item.href} data-testid={`menu-link-${item.key}`} onClick={() => setMenuOpen(false)} className={cls} style={style}>{label}</a>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
            <div className="h-[68px] md:h-[84px]" aria-hidden="true" />
        </>
    );
}

export function SiteBottom() {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const isContactPage = stripLangPrefix(location.pathname) === "/contact";
    const [brochureEmail, setBrochureEmail] = useState("");
    const [brochureStatus, setBrochureStatus] = useState("idle");

    const submitBrochure = async (e) => {
        e.preventDefault();
        const email = brochureEmail.trim();
        if (!/.+@.+\..+/.test(email)) {
            setBrochureStatus("error");
            return;
        }
        try {
            setBrochureStatus("loading");
            const lang = (i18n.language || "en").slice(0, 2).toLowerCase();
            await axios.post(`${API}/contact`, {
                name: email.split("@")[0],
                email,
                interest: "Brochure download",
                message: "Brochure auto-delivery request from site footer.",
                language: lang,
            });
            // Immediate download so the brochure arrives even if email is delayed/spam.
            const link = document.createElement("a");
            link.href = `${API}/brochure?lang=${lang}`;
            link.rel = "noopener";
            document.body.appendChild(link);
            link.click();
            link.remove();
            trackConversion("brochure_download", { language: lang, form_location: "footer" });
            setBrochureStatus("success");
            setBrochureEmail("");
        } catch {
            setBrochureStatus("error");
        }
    };

    return (
        <>
            {!isContactPage && (
                <section data-testid="contact-us-section" className="bg-white pt-6 md:pt-8 pb-14 md:pb-20">
                    <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
                        <a
                            href="mailto:stef@barefootluxury.world"
                            data-testid="contact-us-btn"
                            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#1a1a18] text-[#f5ecd9] text-[11px] md:text-[12px] uppercase tracking-[0.32em] hover:bg-[#b59565] hover:text-[#1a1a18] transition-colors duration-500"
                        >
                            <Mail size={16} strokeWidth={1.6} />
                            {t("landing.contactUs")}
                            <ArrowRight size={14} strokeWidth={1.6} className="transition-transform duration-500 group-hover:translate-x-1" />
                        </a>
                    </div>
                </section>
            )}

            <section data-testid="brochure-section" className="bg-white py-10 md:py-14 border-t border-[#e5d9bf]">
                <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#f3ead9] mb-8">
                        <Download size={18} strokeWidth={1.4} className="text-[#7a4a3a]" />
                    </div>
                    <h2 className="text-[#1a1a18] text-4xl md:text-5xl lg:text-[52px] leading-[1.05] tracking-tight" style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}>
                        {t("landing.brochure.title")}
                    </h2>
                    <p className="mt-5 text-[#5c5a55] text-base md:text-[17px] leading-[1.75] font-light">{t("landing.brochure.subtitle")}</p>
                    <form onSubmit={submitBrochure} data-testid="brochure-form" className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0">
                        <input
                            type="email"
                            value={brochureEmail}
                            onChange={(e) => { setBrochureEmail(e.target.value); if (brochureStatus !== "idle") setBrochureStatus("idle"); }}
                            data-testid="brochure-email-input"
                            placeholder={t("landing.brochure.placeholder")}
                            required
                            aria-label="Email address"
                            className="flex-1 bg-transparent border border-[#e5d9bf] sm:border-r-0 px-5 py-4 text-[15px] text-[#1a1a18] placeholder:text-[#9a958a] focus:outline-none focus:border-[#7a4a3a] transition-colors"
                        />
                        <button
                            type="submit"
                            data-testid="brochure-submit"
                            disabled={brochureStatus === "loading"}
                            className="inline-flex items-center justify-center gap-2 bg-[#1a1a18] hover:bg-[#7a4a3a] disabled:opacity-60 text-white px-7 py-4 text-[12px] uppercase tracking-[0.25em] font-light transition-colors"
                        >
                            {brochureStatus === "loading" ? t("landing.brochure.sending") : brochureStatus === "success" ? t("landing.brochure.sent") : t("landing.brochure.cta")}
                            {brochureStatus !== "loading" && brochureStatus !== "success" && <ArrowRight size={14} strokeWidth={1.4} />}
                        </button>
                    </form>
                    {brochureStatus === "success" && (
                        <div className="mt-5 flex flex-col items-center gap-3">
                            <p className="text-[13px] text-[#7a4a3a] tracking-wide">{t("landing.brochure.success")}</p>
                            <a
                                data-testid="brochure-download-link"
                                href={`${API}/brochure?lang=${(i18n.language || "en").slice(0, 2).toLowerCase()}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-[12px] tracking-[0.15em] uppercase text-[#1a1a18] border-b border-[#1a1a18] pb-1 hover:opacity-70 transition-opacity"
                            >
                                <Download size={14} strokeWidth={1.4} />
                                {t("landing.brochure.downloadNow")}
                            </a>
                        </div>
                    )}
                    {brochureStatus === "error" && (<p className="mt-5 text-[13px] text-[#b04a3a] tracking-wide">{t("landing.brochure.error")}</p>)}
                </div>
            </section>

            <footer data-testid="site-footer" className="bg-white pt-8 pb-16 border-t border-[#e5d9bf]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 flex flex-col items-center text-center">
                    <img src="/assets/logo-footer.png" alt="Cabo Verde" className="h-20 md:h-24 w-auto mb-10" loading="lazy" />
                    <nav aria-label="Mentions légales" className="flex flex-col sm:flex-row flex-wrap items-center justify-center divide-y sm:divide-y-0 sm:divide-x divide-[#cfc6b3] text-[11px] uppercase tracking-[0.25em] font-light text-[#5c5a55]">
                        {[
                            { key: "cookie", href: "/cookie-policy" },
                            { key: "privacy", href: "/privacy-policy" },
                            { key: "terms", href: "/terms-and-conditions" },
                            { key: "disclaimer", href: "/disclaimer" },
                        ].map((link) => (
                            <Link key={link.key} to={link.href} className="px-5 py-3 hover:text-[#1a1a18] transition-colors text-center">
                                {t(`footer.${link.key}`)}
                            </Link>
                        ))}
                        <button
                            type="button"
                            data-testid="footer-cookie-preferences"
                            onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))}
                            className="px-5 py-3 uppercase tracking-[0.25em] hover:text-[#1a1a18] transition-colors text-center"
                        >
                            {t("footer.cookiePreferences")}
                        </button>
                    </nav>
                </div>
            </footer>
        </>
    );
}

export function FloatingCTA() {
    const { t } = useTranslation();
    const location = useLocation();
    if (stripLangPrefix(location.pathname) === "/contact") return null;
    return (
        <Link
            to="/contact"
            data-testid="contact-fab"
            aria-label="Contact us"
            className="fixed bottom-5 right-5 z-50 group hidden sm:flex items-center gap-2.5 sm:gap-3 bg-[#f3ead9] hover:bg-[#ebdfc0] text-[#1a1a18] border border-[#e5d9bf] rounded-full shadow-[0_10px_30px_rgba(26,26,24,0.18)] transition-all hover:scale-[1.03] pl-4 pr-5 py-3 sm:pl-5 sm:pr-6 sm:py-3.5"
        >
            <Mail size={16} strokeWidth={1.6} className="text-[#1a1a18] sm:hidden" aria-hidden="true" />
            <Mail size={18} strokeWidth={1.6} className="text-[#1a1a18] hidden sm:block" aria-hidden="true" />
            <span className="sm:hidden text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap">{t("fab.mobile")}</span>
            <span className="hidden sm:inline text-[11px] md:text-[12px] uppercase tracking-[0.22em] font-medium leading-tight whitespace-nowrap">{t("fab.desktop")}</span>
            <ArrowRight size={12} strokeWidth={1.8} className="text-[#1a1a18] sm:hidden transition-transform duration-500 group-hover:translate-x-1" aria-hidden="true" />
            <ArrowRight size={14} strokeWidth={1.8} className="text-[#1a1a18] hidden sm:block transition-transform duration-500 group-hover:translate-x-1" aria-hidden="true" />
        </Link>
    );
}

export function PageChrome({ children }) {
    return (
        <>
            <SiteHeader />
            {children}
            <SiteBottom />
            <FloatingCTA />
        </>
    );
}
